import {
  ChipBadge,
  IStyles,
  IconWrapper,
  Loader,
  MobilePopup,
  NoContentCard,
  QuestionContainerWithSolution,
  TeacherUploadedTag,
  deserify,
  downloadPdfToAndroid,
  formatErrorMessages,
  formatSecondsToDateTimeString,
  getDeviceType,
  getFileExtensionType,
  getFileSizeInMB,
  getFileType,
  getHumanReadableTimestampString,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  theme,
  uploadFile,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import PersonIcon from '@mui/icons-material/Person';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CloudUpload } from '@protos/learning_management/lms.db_pb';

import { Question } from '@protos/content_management/content.db_pb';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import {
  bloomLevelValuesArray,
  difficultyValuesArray,
  questionValuesArray,
  toBloomLevelEnum,
  toDifficultyEnum,
  toQuestionEnum,
} from 'apps/teacher/src/utils/functions';
import { CustomUploadFileResponse } from '../../Assessment/reducer/assessmentFlow.slice';
import { setToastInfoV2 } from '../../Home/reducer/homeDashboard.slice';
import { AddQuestionsPopup } from '../../Assessment/components/UploadQuestions/AddQuestionPopup';
import { AddPromptPopup } from '../../Assessment/QuestionPaperGenerator/SearchQuestions/components/AddPromptPopup';
import { ValidationPopup } from '../../Assessment/QuestionPaperGenerator/QuestionPaperCreate/components/ValidationPopup';
import { AssessmentCreationEnum } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import styled from '@emotion/styled';
import { File } from 'buffer';
import {
  FileUploadPurpose,
  MimeEnum,
  SignedUrlReponse,
} from '@protos/learning_management/lms.common.apis_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { UploadValidationError } from '@protos/content_management/cms.teacher.questions.apis_pb';
import { getTime } from 'date-fns';
import { Timestamp } from '@bufbuild/protobuf';
import Header from 'apps/teacher/src/components/Header';
import { APIResponse_StatusEnum } from '@protos/common/common.db_pb';
import AiGeneratedButton from '../../Assessment/QuestionPaperGenerator/ReviewQuestionPaper/Buttons/AiGeneratedButton';
import {
  resetManageHWState,
  setAiPromptStatus,
  setQuestionUploadFilter,
} from '../reducer/manageHomework.slice';
import { useGlobalContext } from 'apps/teacher/src/app/Context/GlobalContextProvider';
import { Navigate, useNavigate } from 'react-router-dom';

const styles: IStyles = {
  root: {
    bgcolor: '#F8F8FC',
    boxSizing: 'border-box',
    // overflow: { md: 'hidden', xs: 'auto' },
    px: { xs: pxToRem(20), lg: pxTovW(240) },
  },
  mainContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: pxTovW(40) },
    overflow: 'scroll',
    height: '100%',
  },
  leftPanel: {
    flexBasis: { md: pxTovW(550), lg: pxTovW(615) },
    display: 'flex',
    flexDirection: 'column',
    gap: { md: pxTovW(30) },
  },
  rightPanel: {
    flexBasis: { md: pxTovW(1000) },
    paddingBottom: { xs: pxToRem(70), md: 0 },
    mb: { xs: pxToRem(20), md: pxTovW(80) },
    overflow: { xs: 'initial', md: 'scroll' },
    flexGrow: 1,
    background: '#fff',
    borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
    boxShadow: '0px 0px 10px #E7E7E74A',
    p: { xs: pxToRem(10), md: pxTovW(20) },
    display: 'flex',
    flexDirection: 'column',
    // alignItems: 'flex-start',
    gap: { xs: pxToRem(10), md: pxTovW(30) },
    maxHeight: { md: '70vh' },
    position: 'relative',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(20), md: pxTovW(20) },
    // padding: {
    //   md: `${pxTovW(30)} ${pxTovW(40)}`,
    // },
    // bgcolor: 'common.white',
  },
  heading: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    // borderBottom: { md: '1px solid grey' },
    py: { xs: pxToRem(20), md: pxTovW(30) },
  },
  textBox: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: { xs: pxToRem(10), md: pxTovW(0) },
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    '& > * ': { display: 'inline' },
  },
  questionBank: {
    // bgcolor: 'common.white',
    padding: { md: pxTovW(30) },
    paddingBottom: { xs: pxToRem(70), md: 0 },
    borderRadius: { xs: pxToRem(30), md: pxTovW(30) },
    // border: '1px solid red',
    // overflowY: 'scroll',
  },
  selectContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
  },
};
interface IUploadStatusMessage {
  errors?: UploadValidationError[] | string[];
  fileName?: string;
  successMessage?: string;
}

interface IQuestionUploadFilter {
  value: 'all' | 'teacher' | '';
}
export default function QuestionBank() {
  const [searchText, setSearchText] = useState<string>('');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { class_subject_info } = useAppSelector((state) => state.homeDashboard);
  const { chapter_topic_data } = useAppSelector((state) => state.assessment);
  const [openFileUploadPopup, setOpenFileUploadPopup] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<'loading' | 'success' | 'error'>(
    'success'
  );
  const MAX_FILE_LIMIT_MB = 50;
  const {
    LmsAssessmentTeacherAPIServiceV7WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler,
    TeacherUploadQuestionV1ClientWithStatusCodeHandler,
    LmsCommonAPIServiceV2ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const { ai_prompt_status, question_upload_filter } = useAppSelector(
    (state) => state.manageHomework
  );
  const [difficultyModalState, setDifficultyModalState] = useState(false);
  const [quesTypeModalState, setQuesTypeModalState] = useState(false);
  const [bloomLevelModalState, setBloomLevelModalState] = useState(false);
  const [chapterModalState, setChapterModalState] = useState(false);
  const [topicModalState, setTopicModalState] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(
    null
  );
  const [selectedBloomLevel, setSelectedBloomLevel] = useState<string | null>(
    null
  );
  const [selectedQuestionType, setSelectedQuestionType] = useState<
    string | null
  >(null);
  const [selectedChapterId, setSelectedChapterId] = useState<number | null>(
    null
  );
  const [questionList, setQuestionList] = useState<Question[]>([]);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const selectedBloomLevelEnum = useMemo(() => {
    return selectedBloomLevel ? toBloomLevelEnum(selectedBloomLevel) : null;
  }, [selectedBloomLevel]);

  const selectedQuestionTypeEnum = useMemo(() => {
    return selectedQuestionType ? toQuestionEnum(selectedQuestionType) : null;
  }, [selectedQuestionType]);

  const selectedDifficultyEnum = useMemo(() => {
    return selectedDifficulty ? toDifficultyEnum(selectedDifficulty) : null;
  }, [selectedDifficulty]);

  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const { setSelectedFunction } = useGlobalContext();
  const [FileUploading, setFileUploading] = useState(false);
  const [File, setFile] = useState<CustomUploadFileResponse | undefined>();
  const [addPromptPopupOpen, setAddPromptPopupOpen] = useState<boolean>(false);
  const [uploadStatusMessage, setUploadStatusMessage] =
    useState<IUploadStatusMessage>({});
  const [validationStatus, setValidationStatus] = useState<
    { type: 'success' | 'error'; heading?: string; message: string } | undefined
  >(undefined);
  const leftPanelScrollTopRef = useRef<HTMLDivElement | null>(null);
  const rightPanelScrollTopRef = useRef<HTMLDivElement | null>(null);
  const [templateDownloading, setTemplateDownloading] = useState(false);
  const chapters =
    chapter_topic_data?.chapters?.map((chapter) => {
      return {
        chapterId: chapter.chapterId,
        name: chapter.chapterTitle,
        chapterNo: chapter.chapterNo,
        topicInfo: chapter.topics,
      };
    }) || [];
  const chapterOptions = chapters.map(
    ({ chapterNo, name, chapterId, topicInfo }) => ({
      label: `${chapterNo}. ${name}`,
      value: name,
      chapterId,
      topicInfo: topicInfo ?? [],
    })
  );
  const selectedChapter =
    chapterOptions.find((c) => c.chapterId === selectedChapterId) || null;
  const topicOptions = (
    selectedChapter
      ? selectedChapter.topicInfo
      : chapters.flatMap((c) => c.topicInfo ?? [])
  ).map(({ topicTitle, topicId }) => ({
    label: topicTitle,
    value: topicTitle,
    topicId,
  }));
  const resetTopicId = useCallback(
    (chapterId: number | null) => {
      if (chapterId) {
        if (selectedTopicId) {
          const topic = topicOptions.find((t) => t.topicId === selectedTopicId);
          if (!topic) {
            setSelectedTopicId(null);
          }
        }
      }
    },
    [selectedTopicId, topicOptions]
  );
  useEffect(() => {
    resetTopicId(selectedChapterId);
  }, [selectedChapterId, resetTopicId]);
  const moduleIds = useMemo(() => {
    return selectedTopicId
      ? [selectedTopicId]
      : selectedChapterId
      ? [
          selectedChapterId,
          ...(chapterOptions
            ?.find((c) => c.chapterId === selectedChapterId)
            ?.topicInfo?.map((t) => t.topicId) || []),
        ]
      : chapterOptions
          ?.flatMap((chapter) => [
            chapter.chapterId,
            ...chapter.topicInfo.map((topic) => topic.topicId),
          ])
          ?.sort((a, b) => a - b) || [];
  }, [selectedTopicId, selectedChapterId, chapterOptions]);
  const backButtonClick = async () => {
    dispatch(resetManageHWState());
    navigate(-1);
  };
  useEffect(() => {
    setSelectedFunction(() => backButtonClick);
    return () => {
      setSelectedFunction(null);
    };
  }, []);
  const fetchQuestions = async (
    prompt?: string,
    userFilter?: 'all' | 'teacher',
    page: number = 1,
    search?: string
  ) => {
    const moduleIds = selectedTopicId
      ? [selectedTopicId]
      : selectedChapterId
      ? [
          selectedChapterId,
          ...(chapterOptions
            ?.find((c) => c.chapterId === selectedChapterId)
            ?.topicInfo?.map((t) => t.topicId) || []),
        ]
      : chapterOptions
          ?.flatMap((chapter) => [
            chapter.chapterId,
            ...chapter.topicInfo.map((topic) => topic.topicId),
          ])
          ?.sort((a, b) => a - b) || [];
    try {
      setLoading('loading');
      const response =
        await LmsAssessmentTeacherAPIServiceV7WithStatusCodeHandler.getModuleQuestionsWithFilters(
          {
            teacherId: user_info?.teacherProfileId,
            createdByUserId: userFilter
              ? userFilter === 'teacher'
                ? user_info?.teacherProfileId
                : undefined
              : question_upload_filter?.value === 'teacher'
              ? user_info?.teacherProfileId
              : undefined,
            subjectId: class_subject_info?.subjectId,
            schoolId: userFilter
              ? userFilter === 'teacher'
                ? user_info?.schoolDetails[0]?.schoolId
                : undefined
              : question_upload_filter?.value === 'teacher'
              ? user_info?.schoolDetails[0]?.schoolId
              : undefined,
            moduleIds: moduleIds,
            assessmentCreationType:
              AssessmentCreationEnum.ASSESSMENT_CREATION_HOMEWORK,
            requiredQuestionTypes: selectedQuestionTypeEnum
              ? [selectedQuestionTypeEnum]
              : [],
            requiredDifficultyTypes: selectedDifficultyEnum
              ? [selectedDifficultyEnum]
              : [],
            bloomLevels: selectedBloomLevelEnum ? [selectedBloomLevelEnum] : [],
            aiQuestionPrompts: [prompt || ai_prompt_status?.prompt || ''],
            noOfQuestions:
              ai_prompt_status?.prompt_validated === 'success' ? 10 : undefined,
            isAiAssessment: ai_prompt_status?.prompt_validated === 'success',
            pageNumber: page,
            searchText: search !== undefined ? search : searchText || undefined,
          }
        );
      const quesList =
        ai_prompt_status?.prompt_validated === 'success'
          ? [...(response.data?.questions || []), ...questionList]
          : response.data?.questions || [];
      setQuestionList(quesList);
      const pagination = response.data?.pagination;
      if (pagination) {
        setTotalPages(pagination.totalPages || 1);
        setTotalItems(pagination.totalItems || quesList.length);
        setSelectedPage(pagination.currentPage || page);
      } else {
        setTotalPages(1);
        setTotalItems(quesList.length);
        setSelectedPage(1);
      }
      if (ai_prompt_status?.prompt_validated === 'success') {
        dispatch(
          setToastInfoV2({
            label: `${
              response.data?.questions?.length || 0
            } AI generated questions added to the question bank`,
            open: true,
            variant: 'info',
          })
        );
      }
      setLoading('success');
    } catch (error) {
      setLoading('error');
    }
  };

  const handleValidatePrompt = async () => {
    if (!ai_prompt_status?.prompt) {
      return;
    }
    try {
      setLoading('loading');
      const subjectName = chapter_topic_data?.subjectName || '';
      const res =
        await LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler.checkValidPrompt(
          {
            subject: subjectName,
            moduleIds: moduleIds,
            customPrompt: ai_prompt_status?.prompt,
            teacherId: user_info?.teacherProfileId,
          }
        );
      if (res.isValidated) {
        dispatch(
          setAiPromptStatus({
            ...ai_prompt_status,
            prompt_validated: 'success',
          })
        );
        setValidationStatus({
          type: 'success',
          message: 'Prompt validated! Ready to use.',
        });
      } else {
        dispatch(
          setAiPromptStatus({
            ...ai_prompt_status,
            prompt_validated: 'error',
          })
        );
        setValidationStatus({
          type: 'error',
          heading: 'Invalid Prompt',
          message: (
            (res.message || '') + ' Please review and try again.'
          ).trim(),
        });
      }
    } catch (err) {
      console.log(err);
      dispatch(
        setAiPromptStatus({
          ...ai_prompt_status,
          prompt_validated: 'error',
        })
      );
      setValidationStatus({
        type: 'error',
        heading: 'Some error occurred',
        message: 'Error while validating prompt. Please try after some time',
      });
    } finally {
      setLoading('success');
    }
  };

  const updateFile = async (initialFile: CustomUploadFileResponse) => {
    setFile(initialFile);
  };

  const FileUpload = async (filesToUpload: FileList) => {
    const file = filesToUpload[0];
    console.log('file:', file);
    if (!file) return;
    if (
      ![
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ].includes(file.type)
    ) {
      dispatch(
        setToastInfoV2({
          label: 'Please upload a valid Excel file (.xls or .xlsx)',
          variant: 'error',
          open: true,
        })
      );

      return;
    }

    const tempUserUploads: CustomUploadFileResponse = {
      uploadFileData: new CloudUpload({
        gcpFileUrl: '',
        personId: user_info?.teacherProfileId,
        fileSizeInMb: getFileSizeInMB(file.size),
        isDeleted: false,
        externalWebResourceUrl: '',
        fileName: file.name,
        fileType: getFileType(file.type),
        fileExtensionType: getFileExtensionType(file.type),
        isUploaded: false,
        signedUrl: '',
      }),
      fileUploadStatus: 'pending',
      fileSize: file.size,
    };

    await updateFile(tempUserUploads);

    //! max file size to be 50 MB
    if (getFileSizeInMB(file.size) > MAX_FILE_LIMIT_MB) {
      tempUserUploads.uploadFileData.isUploaded = false;
      tempUserUploads.fileUploadStatus = 'Size Exceeded';
      dispatch(
        setToastInfoV2({
          label: 'File Size should be less than 50 MB',
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    setFileUploading(true);
    try {
      const signedUrlResponse: SignedUrlReponse =
        await LmsCommonAPIServiceV2ClientWithStatusCodeHandler.getSignedUrlForFileUpload(
          {
            personId: user_info?.teacherProfileId,
            filesInfo: [
              {
                originalFilePath: file.name,
                mimeType: MimeEnum.MIME_XLSX,
                size:
                  getFileSizeInMB(file.size) < 0.01
                    ? 0.01
                    : getFileSizeInMB(file.size),
              },
            ],
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            fileUploadPurpose: FileUploadPurpose.FILE_UPLOAD_TEACHER_QUESTION,

            schoolId: user_info?.schoolDetails[0]?.schoolId || undefined,
          }
        );

      if (signedUrlResponse && signedUrlResponse.response.length > 0) {
        const signedUrl = getMediaBasePath(
          signedUrlResponse.response[0].signedUrl
        );
        const uploadSuccess = await uploadFile({ file, signedUrl });
        if (uploadSuccess.success === true) {
          tempUserUploads.uploadFileData.signedUrl = signedUrl;
          tempUserUploads.uploadFileData.isUploaded = true;
          tempUserUploads.fileUploadStatus = 'success';

          const response =
            await TeacherUploadQuestionV1ClientWithStatusCodeHandler.upsertTeacherQuestions(
              {
                gcsFilePath: signedUrl,
                schoolId: user_info?.schoolDetails[0]?.schoolId,
                teacherId: user_info?.teacherProfileId,
                subjectId: class_subject_info?.subjectId,
                sectionId: class_subject_info?.sectionId,
              }
            );

          if (response.status === APIResponse_StatusEnum.STATUS_SUCCESS) {
            tempUserUploads.uploadFileData.isUploaded = true;
            tempUserUploads.fileUploadStatus = 'success';
            setUploadStatusMessage({
              successMessage: response.message || 'File uploaded successfully',
            });
            fetchQuestions();
          } else {
            console.log(
              'response.validationErrors:',
              response.validationErrors
            );
            tempUserUploads.uploadFileData.isUploaded = false;
            tempUserUploads.fileUploadStatus = 'Upload Error';
            setUploadStatusMessage({ errors: response.validationErrors });
          }
        } else {
          tempUserUploads.uploadFileData.isUploaded = false;
          tempUserUploads.fileUploadStatus = 'Upload Error';
          dispatch(
            setToastInfoV2({
              label: `Error Occurred! Unable to upload the file.`,
              variant: 'error',
              open: true,
            })
          );
        }
        setFileUploading(false);
      } else {
        tempUserUploads.uploadFileData.isUploaded = false;
        tempUserUploads.fileUploadStatus = 'Upload Error';
        setUploadStatusMessage({ errors: ['Failed to get signed URL'] });
      }
    } catch (error: any) {
      console.log('error:', error);
      setFileUploading(false);
      tempUserUploads.uploadFileData.isUploaded = false;
      tempUserUploads.fileUploadStatus = 'Upload Error';
      setUploadStatusMessage({ errors: [formatErrorMessages(error.message)] });
    }
  };
  const downloadTemplate = async () => {
    // setTemplateDownloaded(true);
    setTemplateDownloading(true);
    try {
      dispatch(
        setToastInfoV2({
          label: `Downloading template.`,
          variant: 'success',
          open: true,
        })
      );

      const response =
        await TeacherUploadQuestionV1ClientWithStatusCodeHandler.downloadTeacherQuestionsTemplate(
          {
            subjectId: class_subject_info?.subjectId,
            teacherId: user_info?.teacherProfileId,
            sectionId: class_subject_info?.sectionId,
            schoolId: user_info?.schoolDetails[0]?.schoolId,
          }
        );

      console.log('response:', response);
      if (response.signedUrl) {
        if (getDeviceType() === 'android') {
          downloadPdfToAndroid(response.signedUrl);
        } else {
          const link = document.createElement('a');
          link.href = response.signedUrl;
          link.setAttribute('download', 'Question_Upload_Template.xlsx'); //or any other extension
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        setTemplateDownloading(true);
      } else {
        dispatch(
          setToastInfoV2({
            label: `Error Occurred! Unable to download template.`,
            variant: 'error',
            open: true,
          })
        );
      }

      setTemplateDownloading(false);
    } catch (error) {
      // console.log('error:', error);
      dispatch(
        setToastInfoV2({
          label: `Error Occurred! Unable to download template.`,
          variant: 'error',
          open: true,
        })
      );
      setTemplateDownloading(false);
    }
  };
  const handleDelete = async (questionId: string) => {
    try {
      setLoading('loading');
      await TeacherUploadQuestionV1ClientWithStatusCodeHandler.deleteTeacherQuestions(
        {
          teacherId: user_info?.teacherProfileId,
          schoolId: user_info?.schoolDetails[0]?.schoolId,
          questionIds: [questionId],
        }
      );
      dispatch(
        setToastInfoV2({
          label: 'Question deleted successfully',
          variant: 'info',
          open: true,
        })
      );
      setLoading('success');
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      dispatch(
        setToastInfoV2({
          label: 'Error deleting question',
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setLoading('success');
    }
  };
  useEffect(() => {
    if (leftPanelScrollTopRef.current) {
      leftPanelScrollTopRef.current.scrollTop = 0;
    }
    if (rightPanelScrollTopRef.current) {
      rightPanelScrollTopRef.current.scrollTop = 0;
    }
  }, [selectedPage]);

  useEffect(() => {
    fetchQuestions(undefined, undefined, 1, '');
    setSearchText('');
  }, [
    selectedChapterId,
    selectedTopicId,
    class_subject_info?.subjectId,
    selectedBloomLevel,
    selectedDifficulty,
    selectedQuestionType,
  ]);

  const renderSelectMenu = (
    optionList: string[],
    placeholder: string,
    value: string | null,
    onChange: (e: any, value: string | null) => void,
    onMobileClick: () => void,
    modalState: boolean,
    setModalState: (val: boolean) => void,
    disabled?: boolean
  ) => {
    if (isMobile) {
      return (
        <>
          <MobilePopup
            options={optionList}
            title={placeholder}
            modalState={modalState}
            setModalState={setModalState}
            onChange={onChange}
            clearall
          />
          <Box
            sx={{
              display: 'flex',
              height: {
                md: pxTovW(55),
                xs: pxToRem(38),
              },
              border: '1px solid #38FF92',
              borderRadius: '7px',
              boxSizing: 'border-box',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: pxToRem(10), md: pxTovW(20) },
              width: '100%',
              opacity: disabled ? 0.5 : 1,
            }}
            onClick={disabled ? undefined : onMobileClick}
          >
            <Typography
              // {...params}
              variant="h4"
              sx={{
                px: pxToRem(5),
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {value || placeholder}
            </Typography>
            <KeyboardArrowDownRoundedIcon
              sx={{
                color: 'text.disabled',
              }}
            />
          </Box>
        </>
      );
    }
    return (
      <Autocomplete
        disablePortal
        id="combo-box-demo"
        popupIcon={
          <KeyboardArrowDownRoundedIcon
            sx={{
              color: 'text.disabled',
              fontSize: { xs: 'small', md: 'large' },
            }}
          />
        }
        options={optionList}
        value={value}
        onChange={onChange}
        disabled={disabled}
        // disableClearable
        sx={{
          border: '1px solid #38FF92',
          borderRadius: '7px',
          width: '100%',
          '& .MuiInputBase-root': {
            backgroundColor: 'initial',
            '&:hover': {
              backgroundColor: 'initial',
            },
            '&:focus': {
              backgroundColor: 'initial',
            },
            '&:hover::before': {
              borderBottom: 'none',
            },
            '&:focus::before': {
              borderBottom: 'none',
            },
            '&::before': {
              borderBottom: 'none',
            },
            '&::after': {
              borderBottom: 'none',
            },
          },
        }}
        renderInput={(params) => (
          <TextField variant="filled" {...params} label={placeholder} />
        )}
      />
    );
  };

  if (loading === 'loading') {
    return <Loader />;
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.heading}>
        {/* <Typography variant="h1">My Homework</Typography> */}
        <Box sx={styles.textBox}>
          <Typography variant="h2">Question Bank </Typography>
          <Typography variant="h3">
            <ChipBadge
              label={totalItems}
              color="primary"
              size="small"
            />
          </Typography>
        </Box>
        <Select
          value={question_upload_filter?.value}
          onChange={(e) => {
            dispatch(
              setQuestionUploadFilter({
                value: e.target.value as 'all' | 'teacher',
              })
            );
            fetchQuestions(undefined, e.target.value as 'all' | 'teacher');
          }}
          displayEmpty
          renderValue={() => {
            if (!question_upload_filter?.value) {
              return <Typography color="text.disabled">Select</Typography>;
            }
            return question_upload_filter?.value === 'all' ? (
              <Typography variant="cardText"> All Questions</Typography>
            ) : (
              <Typography variant="cardText">
                {' '}
                Teacher Uploaded Questions
              </Typography>
            );
          }}
          sx={{
            background: '#F5FAFF',
            width: { xs: '100%', md: pxTovW(332) },
            borderRadius: { xs: pxToRem(10), md: pxTovW(5) },
            height: { xs: 'max-content', md: '70%', lg: pxTovW(65) },
            '.MuiOutlinedInput-notchedOutline': { border: '2px solid #87C5FF' },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: '2px solid #87C5FF',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '2px solid #87C5FF',
            },
            boxShadow: 'none',
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                position: 'sticky',
                maxHeight: { xs: pxToRem(100), md: pxTovW(300) },
                minHeight: { xs: 'max-content', md: 'max-content' },
                backgroundColor: '#F8FCFF',
                '&::-webkit-scrollbar': { display: 'none' },
                padding: 0,
                borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
                '& .MuiList-root': {
                  backgroundColor: '#F8FCFF',
                  maxHeight: { xs: pxToRem(150), md: pxTovW(300) },
                  padding: 0,
                },

                minWidth: { xs: '95%', md: pxTovW(230) },
                maxWidth: { xs: '95%', md: pxTovW(200), lg: pxTovW(150) },
              },
            },
          }}
        >
          <MenuItem value="all">All Questions</MenuItem>
          <MenuItem value="teacher">Teacher Uploaded Questions</MenuItem>
        </Select>
      </Box>
      <Box sx={styles.mainContainer}>
        <Box sx={styles.leftPanel} ref={leftPanelScrollTopRef}>
          <Box sx={styles.header}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: { xs: pxToRem(10), md: pxTovW(20) },
                backgroundColor: 'common.white',
                borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
                boxShadow: `0px 0px ${pxToRem(5)} #E7E7E7D9`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: pxToRem(10), md: pxTovW(20) },
                  paddingBottom: { xs: pxToRem(10), md: pxTovW(20) },
                  marginBottom: { xs: pxToRem(10), md: pxTovW(20) },
                }}
              >
                <Box sx={{ display: 'flex', gap: { xs: pxToRem(8), md: pxTovW(12) }, alignItems: 'center' }}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') fetchQuestions(undefined, undefined, 1, searchText);
                    }}
                    value={searchText}
                    placeholder="Search concepts or subtopics"
                    sx={{
                      border: '1px solid #007CDC',
                      borderRadius: {
                        xs: pxToRem(8),
                        md: pxTovW(15),
                      },
                      '& .MuiOutlinedInput-root': {
                        fontWeight: '700',
                        color: 'black',
                        px: 0,
                        p: {
                          xs: pxToRem(8),
                          md: `${pxToRem(15)} ${pxToRem(20)}`,
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (searchText === '' ||
                        searchText === null) && (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{
                              fontSize: {
                                xs: pxToRem(18),
                                md: pxToRem(25),
                              },
                            }}
                            color="primary"
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box
                    onClick={() => fetchQuestions(undefined, undefined, 1, searchText)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                      bgcolor: '#007CDC',
                      borderRadius: { xs: pxToRem(8), md: pxTovW(10) },
                      px: { xs: pxToRem(12), md: pxTovW(18) },
                      height: { xs: pxToRem(40), md: pxTovW(52) },
                      '&:hover': { bgcolor: '#005FAD' },
                    }}
                  >
                    <SearchIcon sx={{ color: '#fff', fontSize: { xs: pxToRem(20), md: pxToRem(22) } }} />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: pxToRem(10), md: pxTovW(20) },
                  }}
                >
                  {renderSelectMenu(
                    difficultyValuesArray,
                    'Difficulty Level',
                    selectedDifficulty ?? null,
                    async (e, value) => {
                      setSelectedDifficulty(value);
                    },
                    () => setDifficultyModalState(true),
                    difficultyModalState,
                    setDifficultyModalState
                  )}
                  {renderSelectMenu(
                    questionValuesArray,
                    'Question Type',
                    selectedQuestionType ?? null,
                    async (e, value) => {
                      setSelectedQuestionType(value);
                    },
                    () => setQuesTypeModalState(true),
                    quesTypeModalState,
                    setQuesTypeModalState
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: pxToRem(10), md: pxTovW(20) },
                  }}
                >
                  {renderSelectMenu(
                    bloomLevelValuesArray,
                    'Bloom Level',
                    selectedBloomLevel ?? null,
                    async (e, value) => {
                      setSelectedBloomLevel(value);
                    },
                    () => setBloomLevelModalState(true),
                    bloomLevelModalState,
                    setBloomLevelModalState
                  )}
                  {/* empty box so that above box takes 50% width */}
                  <Box sx={{ width: '100%' }} />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: pxToRem(10), md: pxTovW(20) },
                    borderTop: '1px dashed #E3E3E3',
                    pt: { xs: pxToRem(10), md: pxTovW(20) },
                  }}
                >
                  <Box sx={styles.selectContainer}>
                    <Typography variant="h4" fontWeight={'bold'}>
                      Search by Chapter
                    </Typography>
                    {renderSelectMenu(
                      chapterOptions.map((e) => e.label),
                      'Chapter',
                      chapterOptions.find(
                        (e) => e.chapterId === selectedChapterId
                      )?.label ?? null,
                      async (e, value) => {
                        setSelectedChapterId(
                          chapterOptions.find((e) => e.label === value)
                            ?.chapterId || null
                        );
                      },
                      () => setChapterModalState(true),
                      chapterModalState,
                      setChapterModalState
                    )}
                  </Box>
                  <Box sx={styles.selectContainer}>
                    <Typography variant="h4" fontWeight={'bold'}>
                      Search by Topic
                    </Typography>
                    {renderSelectMenu(
                      topicOptions.map((e) => e.label),
                      'Topic',
                      topicOptions.find((e) => e.topicId === selectedTopicId)
                        ?.label ?? null,
                      async (e, value) => {
                        setSelectedTopicId(
                          topicOptions.find((e) => e.label === value)
                            ?.topicId || null
                        );
                      },
                      () => setTopicModalState(true),
                      topicModalState,
                      setTopicModalState
                    )}
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      gap: { xs: pxToRem(10), md: pxTovW(20) },
                    }}
                  >
                    {/* <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          p: {
                            xs: `${pxToRem(8)} ${pxToRem(10)}`,
                            md: `${pxTovW(10)} ${pxTovW(12)}`,
                          },
                          borderRadius: { xs: pxToRem(5), md: pxTovW(10) },
                          border: '1px solid #007CDC',
                          cursor: 'pointer',
                          background: '#CFFFE5',
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: pxToRem(5), md: pxTovW(10) },
                        }}
                        onClick={() => {
                          setAddPromptPopupOpen(true);
                        }}
                      >
                        <IconWrapper
                          name="plus-green"
                          customSx={{ height: pxToRem(23), width: pxToRem(23) }}
                          type="png"
                          parentFolder="icons"
                        />
                        <Typography
                          variant="h4"
                          color={'#0AA34F'}
                          fontWeight={'600'}
                        >
                          Add Questions with AI
                        </Typography>
                      </Box>
                    </Box> */}

                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          p: {
                            xs: `${pxToRem(8)} ${pxToRem(10)}`,
                            md: `${pxTovW(10)} ${pxTovW(12)}`,
                          },
                          borderRadius: { xs: pxToRem(5), md: pxTovW(10) },
                          border: '1px solid #007CDC',
                          cursor: 'pointer',
                          background: '#CFFFE5',
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: pxToRem(5), md: pxTovW(10) },
                        }}
                        onClick={() => {
                          setOpenFileUploadPopup(true);
                        }}
                      >
                        <IconWrapper
                          name="plus-blue"
                          customSx={{ height: pxToRem(23), width: pxToRem(23) }}
                          type="png"
                          parentFolder="icons"
                        />
                        <Typography
                          variant="h4"
                          color={'#007CDC'}
                          fontWeight={'600'}
                        >
                          Add Your Own Questions
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.rightPanel} ref={rightPanelScrollTopRef}>
          {questionList.length > 0 ? (
            questionList.map((q, index) => (
                <Box key={'ques_' + index}>
                  <QuestionContainerWithSolution
                    question={q}
                    hideMarks
                    showQuestionStats
                    showAnswer
                    imageResizeEnabled={true}
                    key={'ques_' + index}
                    headerRightElement={
                      q.createdBy === user_info?.teacherProfileId ? (
                        <IconWrapper
                          name={'deletehw'}
                          size="md"
                          type="png"
                          parentFolder="icons"
                          customSx={{
                            width: {
                              xs: pxToRem(17),
                              md: pxTovW(35),
                              lg: pxTovW(24),
                            },
                            margin: { xs: pxToRem(10), md: 0 },
                          }}
                          onClick={() => handleDelete(q.questionId || '')}
                        />
                      ) : (
                        <> </>
                      )
                    }
                    headerLeftElement={
                      <>
                        {q.isAiGenerated ? <AiGeneratedButton /> : null}
                        {q.createdBy === user_info?.teacherProfileId ? (
                          <TeacherUploadedTag
                            teacherName={user_info?.userName}
                            createdOn={q.createdOn || new Timestamp()}
                          />
                        ) : (
                          <> </>
                        )}
                      </>
                    }
                  />
                </Box>
              ))
          ) : (
            <NoContentCard
              variant="info"
              icon="cards"
              text="No Questions Available"
            />
          )}
          {totalPages > 1 && (
            <Pagination
              page={selectedPage}
              count={totalPages}
              color="primary"
              onChange={async (
                event: React.ChangeEvent<unknown>,
                page: number
              ) => {
                fetchQuestions(undefined, undefined, page);
              }}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'fixed',
                bottom: 0,
                left: { xs: pxToRem(10), md: 'unset' },
                border: '1px solid #007CDC',
                borderTopLeftRadius: { xs: pxToRem(10), md: pxTovW(16) },
                borderTopRightRadius: { xs: pxToRem(10), md: pxTovW(16) },
                boxShadow: '0px 0px 10px #00000029',

                width: { xs: '95%', md: pxTovW(950), lg: pxTovW(840) },
                height: { xs: pxToRem(55), md: pxTovW(85) },
                marginX: 'auto',
                mb: pxTovW(10),
                backgroundColor: '#E9F5FF',
              }}
            />
          )}
        </Box>
      </Box>

      <AddPromptPopup
        open={addPromptPopupOpen}
        value={ai_prompt_status?.prompt}
        onChange={(val) => {
          dispatch(
            setAiPromptStatus({
              prompt: val,
              prompt_validated: undefined,
            })
          );
        }}
        handleClose={() => {
          dispatch(setAiPromptStatus(undefined));
          setAddPromptPopupOpen(false);
        }}
        disabled={
          !ai_prompt_status?.prompt ||
          ai_prompt_status?.prompt_validated !== 'success'
        }
        addQuestionsHandler={() => {
          fetchQuestions();
          dispatch(setAiPromptStatus(undefined));
          setAddPromptPopupOpen(false);
        }}
        validationEnabled={true}
        validated={ai_prompt_status?.prompt_validated}
        onValidateClick={handleValidatePrompt}
      />

      <ValidationPopup
        open={!!validationStatus}
        handleClose={() => {
          setValidationStatus(undefined);
          setAddPromptPopupOpen(true);
        }}
        okHandler={() => {
          setValidationStatus(undefined);
          setAddPromptPopupOpen(true);
        }}
        iconname={
          validationStatus?.type === 'success'
            ? 'check-green'
            : validationStatus?.type === 'error'
            ? 'wrong'
            : undefined
        }
        text1={validationStatus?.heading}
        text2={validationStatus?.message}
      />

      <AddQuestionsPopup
        open={openFileUploadPopup}
        handleClose={() => {
          setFileUploading(false);
          setFile(undefined);
          setUploadStatusMessage({});
          setOpenFileUploadPopup(false);
        }}
        retryUploadFunction={() => {
          setFileUploading(false);
          setFile(undefined);
          setUploadStatusMessage({});
        }}
        downloadClickHandler={downloadTemplate}
        templateDownloading={templateDownloading}
        instructions={[
          'Download the question template from the Download Template button',
          'Fill in your questions following the format specified in the template',
          'Ensure all required fields are completed (question text, options, correct answer, difficulty level)',
          'Save the file in .xlsx or .csv format',
          'Upload the completed file using the upload section below',
          'Review the uploaded questions before adding them to your question bank',
        ]}
        userUploadedFile={File}
        FileUploading={FileUploading}
        handleChangeFunction={(event) => {
          FileUpload(event.target.files as FileList);
          event.target.value = '';
        }}
        uploadStatusMessage={uploadStatusMessage}
        setUploadStatusMessage={setUploadStatusMessage}
      />
    </Box>
  );
}
